import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { EyeIcon, EyeOffIcon, SendIcon } from "./icons";

// Two flows in one modal, distinguished by whether the client has a
// usable login (`client.hasLogin`):
//   1. No login yet → "Invite" form. Bureau sets the email + password
//      directly (or leaves the password blank to auto-generate one);
//      backend creates the credentials and emails them.
//   2. Login exists → "Reset password" form. Bureau picks the new
//      password; backend rotates it and re-sends the credentials email.
//
// On success the modal surfaces the final credentials so the bureau can
// also share them manually via WhatsApp.
const InviteClientModal = ({ isOpen, client, onClose, onInvite, onAfterAction }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Result of the last action: { email, password, link: null }.
  // `link` is kept for forward-compat but is always null now that the
  // invite flow sets the password directly instead of issuing a
  // one-time link.
  const [sentCreds, setSentCreds] = useState(null);
  const [emailDelivered, setEmailDelivered] = useState(false);
  const { setGlobalLoadingMessage } = useLoading();

  // `hasLogin` is set on the bureau-side response when the PublicUser
  // has a password set (the unified profile means "has activated their
  // account").
  const alreadyInvited = Boolean(client?.hasLogin);
  const mode = alreadyInvited ? "reset" : "invite";

  useEffect(() => {
    if (isOpen && client) {
      setEmail(client.email || client.user?.email || "");
      setPassword("");
      setConfirmPassword("");
      setError(null);
      setSentCreds(null);
      setEmailDelivered(false);
    }
  }, [isOpen, client]);

  if (!isOpen || !client) return null;

  const clientName = client.user?.fullName || "Client";
  const clientPhone = (client.user?.contactNumber || "").replace(/\D/g, "");
  const loginUrl =
    (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, "") +
    "/login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (mode === "invite" && !email.trim()) {
      setError("Email is required");
      return;
    }
    if (password && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setIsSubmitting(true);
      setGlobalLoadingMessage(
        mode === "reset" ? "Resetting password..." : "Creating credentials..."
      );

      let response;
      if (mode === "invite") {
        // Bureau sets credentials manually (email + password, or password
        // auto-generated if left blank). Backend creates them on the
        // PublicUser and sends the credentials email; bureau can ALSO
        // share via WhatsApp from the success panel.
        response = await onInvite(client._id, {
          email: email.trim(),
          password: password || undefined,
          confirmPassword: confirmPassword || undefined,
        });
      } else {
        // Bureau-owned password rotation. No email change here — the
        // login email stays the same; we only reset the credential.
        response = await apiFetch(`/clients/${client._id}/reset-password`, {
          method: "POST",
          body: JSON.stringify({
            password: password || undefined,
            confirmPassword: confirmPassword || undefined,
          }),
        });
      }

      const data = response?.data || {};
      setSentCreds({
        email:
          data.email ||
          email.trim() ||
          client.email ||
          client.user?.email ||
          "",
        // Both invite and reset return the password used (server-picked
        // when bureau left it blank, otherwise the typed one).
        password: data.password || password || null,
        link: null,
      });
      setEmailDelivered(Boolean(data.emailSent));
      if (typeof onAfterAction === "function") {
        try {
          onAfterAction(mode, response);
        } catch (_) {
          /* non-fatal */
        }
      }
    } catch (err) {
      setError(err.message || "Action failed");
    } finally {
      setIsSubmitting(false);
      setGlobalLoadingMessage("");
    }
  };

  const buildWhatsAppMessage = () => {
    if (!sentCreds) return "";
    const lines = [
      `As-salamu alaykum ${clientName},`,
      "",
    ];
    if (mode === "invite") {
      lines.push(
        `${client?.bureau?.name || "Your bureau"} has created an account for you on Phuphoo.`
      );
      lines.push("");
    } else {
      lines.push(`Your Phuphoo account password has been reset.`);
      lines.push("");
    }
    lines.push(`Login details:`);
    lines.push(`Email: ${sentCreds.email}`);
    if (sentCreds.password) lines.push(`Password: ${sentCreds.password}`);
    lines.push("");
    lines.push(`Sign in: ${loginUrl}`);
    lines.push("");
    lines.push("Please change your password after signing in.");
    return lines.join("\n");
  };

  const whatsappUrl = () => {
    const message = encodeURIComponent(buildWhatsAppMessage());
    // wa.me with a phone routes the chat to that exact number; without,
    // it opens WhatsApp's contact picker. We prefer the direct route
    // when we know the client's contact.
    return clientPhone
      ? `https://wa.me/${clientPhone}?text=${message}`
      : `https://wa.me/?text=${message}`;
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // No-op — clipboard can fail on insecure origins.
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {/* Card is height-bounded to 90vh and a flex column so the header
          and footer stay pinned while the body scrolls. Without this,
          taller content (success panel + WhatsApp button + helper text)
          pushes the action buttons off-screen on shorter viewports. */}
      <div className="relative w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden bg-[var(--color-surface)] rounded-xl shadow-xl">
        {/* HEADER — always visible */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--color-border)] flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
              {mode === "reset" ? "Reset Client Password" : "Invite Client"}
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              {mode === "reset"
                ? "Rotate the password on this client's existing login."
                : "Create a login account for this client."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* BODY — scrolls when content overflows */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-2">
              Client
            </label>
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-sm text-[var(--color-text)]">
              {clientName}
              <div className="text-[var(--color-text-muted)]">
                ID: {client.user?.referenceId || "—"}
              </div>
              {alreadyInvited && (
                <div className="text-[var(--color-text-muted)] mt-1">
                  Login: {client.email || client.user?.email}
                </div>
              )}
            </div>
          </div>

          {/* Email field — only when first invite. On reset the email is
              fixed (it's the existing login). */}
          {mode === "invite" && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                placeholder="client@example.com"
                required
              />
            </div>
          )}

          {/* Password fields are present in BOTH modes. The bureau sets
              the credentials manually; leaving the field blank generates
              a temporary password server-side. The success panel then
              shows the final credentials so they can be shared via email
              (auto) or WhatsApp (button). */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-2">
              {mode === "reset" ? "New password" : "Password"}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pr-10 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                placeholder="Leave blank to generate a temporary password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                tabIndex="-1"
              >
                {showPassword ? (
                  <EyeOffIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-2">
              Confirm password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pr-10 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                placeholder="Confirm password (or leave blank)"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((s) => !s)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                tabIndex="-1"
              >
                {showConfirmPassword ? (
                  <EyeOffIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {error && <div className="text-sm text-red-500">{error}</div>}

          {/* Success panel — shows credentials/link + WhatsApp share. */}
          {sentCreds && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900 space-y-3">
              <div className="font-semibold">
                {mode === "reset"
                  ? "Password reset successfully."
                  : "Credentials created."}
                {emailDelivered
                  ? " Credentials emailed."
                  : " Email delivery failed — share manually below."}
              </div>

              {/* One "Copy" icon at the top right of the credentials
                  panel — copies the full WhatsApp-ready message in a
                  single click instead of forcing the bureau to copy each
                  field separately. */}
              <div className="relative bg-white rounded-md p-2 pr-9 border border-emerald-200 space-y-1">
                <button
                  type="button"
                  onClick={() => handleCopy(buildWhatsAppMessage())}
                  aria-label="Copy message"
                  title="Copy WhatsApp-ready message"
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
                <div className="font-mono">{sentCreds.email}</div>
                {sentCreds.password && (
                  <div className="font-mono">{sentCreds.password}</div>
                )}
              </div>

              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-semibold transition-colors"
              >
                <SendIcon className="h-4 w-4" />
                Share via WhatsApp
                {clientPhone && (
                  <span className="text-xs opacity-80">
                    (to {clientPhone})
                  </span>
                )}
              </a>
              <p className="text-xs text-emerald-800/80">
                Opens WhatsApp with a pre-written message containing the
                credentials. {clientPhone
                  ? "It will be addressed to the client's saved number."
                  : "WhatsApp will let you pick the contact."}
              </p>
            </div>
          )}
          </div>

          {/* FOOTER — always visible regardless of body scroll position */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
            >
              {sentCreds ? "Done" : "Cancel"}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {isSubmitting
                ? mode === "reset"
                  ? "Resetting..."
                  : "Creating..."
                : sentCreds
                ? mode === "reset"
                  ? "Reset again"
                  : "Re-create"
                : mode === "reset"
                ? "Reset password"
                : "Create credentials"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteClientModal;
